/**
 * Does the chosen machine have room for one more session, and what can the person do when it does
 * not (ADR 0063 step 5). Pure: the dialog renders it, the tests read it. Nothing is ever blocked:
 * the person chooses to start elsewhere, to queue it until there is room, or to start now anyway.
 */
import { ADMISSION_STATE_LABEL, hasRoom } from '@orgus/protocol';
import type { Id, RoomVerdict } from '@orgus/protocol';
import type { StatusTone } from '@orgus/ui';
import { admissionDetail, admissionTone } from '../scheduler/model.ts';

export type RoomChoice = 'move' | 'room-here' | 'room-any' | 'now';

export interface RoomOption {
  value: RoomChoice;
  label: string;
  description: string;
  disabled?: boolean;
}

export interface RoomPlan {
  /** The chosen machine's verdict; null when the hub has no scheduler or the machine is unknown. */
  verdict: RoomVerdict | null;
  /** One quiet line: "Has room: room for 4 more sessions". */
  line: { tone: StatusTone; label: string; detail: string } | null;
  /** Full or not taking work: the person picks one of `options`. */
  needsChoice: boolean;
  /** The best other online machine with room and a checkout of the project. */
  alternative: RoomVerdict | null;
  options: RoomOption[];
  defaultChoice: RoomChoice | null;
}

const NONE: RoomPlan = { verdict: null, line: null, needsChoice: false, alternative: null, options: [], defaultChoice: null };

export function roomPlan(verdicts: readonly RoomVerdict[] | null, machineId: Id): RoomPlan {
  const verdict = verdicts?.find((v) => v.machineId === machineId) ?? null;
  if (!verdict || !verdict.online) return { ...NONE, verdict };
  const a = verdict.admission;
  const line = { tone: admissionTone(a.state), label: ADMISSION_STATE_LABEL[a.state], detail: admissionDetail(a) };
  if (hasRoom(a)) return { ...NONE, verdict, line };

  const alternative = verdicts!.find((v) => v.machineId !== machineId && v.online && v.hasCheckout && hasRoom(v.admission)) ?? null;
  const others = verdicts!.filter((v) => v.machineId !== machineId && v.hasCheckout).length;
  const drained = a.state === 'closed' && /^Drained/.test(a.text);
  const options: RoomOption[] = [
    ...(alternative
      ? [{ value: 'move' as const, label: `Start on ${alternative.name}`, description: `${ADMISSION_STATE_LABEL[alternative.admission.state]}: ${admissionDetail(alternative.admission)}` }]
      : []),
    { value: 'room-here', label: `Start when ${verdict.name} has room`, description: 'Queued as a mission that starts by itself.' },
    ...(others
      ? [{ value: 'room-any' as const, label: 'Start when any machine has room', description: 'On the first machine with the project that has room.' }]
      : []),
    ...(drained ? [] : [{ value: 'now' as const, label: 'Start now anyway', description: a.work ? 'It runs beside the queued heavy work; checks there take longer.' : 'It runs beside what is already there.' }]),
  ];
  const first = options.find((o) => !o.disabled)?.value ?? null;
  return { verdict, line, needsChoice: true, alternative, options, defaultChoice: first };
}

/** The dialog's primary button for a choice. */
export function choiceButton(choice: RoomChoice | null, plan: RoomPlan, fallback: string): string {
  if (!plan.needsChoice || !choice || choice === 'now') return fallback;
  if (choice === 'move' && plan.alternative) return `Start on ${plan.alternative.name}`;
  return 'Queue session';
}
